import { BugReportCard } from '@/components/features/BugReports/tables/BugReportCard';
import { BugReportTable } from '@/components/features/BugReports/tables/BugReportTable';
import { type BugReportListItem } from '@/components/features/BugReports/tables/types';
import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { useDebounce } from '@/hooks/use-debounce';
import { useIsMobile } from '@/hooks/use-mobile';
import { useServerFilters } from '@/hooks/use-server-filters';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type PageProps, type PaginationMeta } from '@/types';
import { Head, usePage } from '@inertiajs/react';
import { AlertCircle, CheckCircle2, Clock, Download, TrendingUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { route } from 'ziggy-js';

interface BugReportsIndexPageProps extends PageProps {
    reports: BugReportListItem[];
    pagination: PaginationMeta;
    filters: Record<string, string | number | undefined>;
    filter_options: {
        statuses: string[];
        severities: string[];
        priorities: string[];
        users: Array<{ id: string; name: string }>;
    };
    metrics: {
        new: number;
        in_review: number;
        critical: number;
        resolved_this_month: number;
    };
    can_see_all: boolean;
    [key: string]: unknown;
}

type FilterColumn = 'status' | 'severity' | 'reporter' | 'assignee';

const filterBackendKey: Record<FilterColumn, string> = {
    status: 'status',
    severity: 'severity',
    reporter: 'reporter_user_id',
    assignee: 'assigned_to_user_id',
};

// Table column id -> the sort key the backend understands.
const columnToBackendSort: Record<string, string> = {
    public_id: 'public_id',
    status: 'status',
    module: 'module',
    created: 'created_at',
};

// Backend sort key -> the table column id (for the active-sort arrow).
const backendSortToColumn: Record<string, string> = {
    public_id: 'public_id',
    status: 'status',
    module: 'module',
    created_at: 'created',
};

export default function BugReportsIndexPage() {
    const { t } = useTranslations();
    const { props } = usePage<BugReportsIndexPageProps>();
    const { reports, pagination, filters, filter_options: filterOptions, metrics, can_see_all: canSeeAll } = props;
    const { applyFilters, loading } = useServerFilters(filters, '/admin/bug-reports');
    const isMobile = useIsMobile();

    const abilities = (props.auth as { abilities?: string[] })?.abilities ?? [];
    const canEdit = abilities.includes('bug-reports.update');
    const canExport = abilities.includes('bug-reports.export');

    const statusLabel = (value: string) => t(`bug_reports.status.${value}`, value);
    const severityLabel = (value: string) => t(`bug_reports.severity.${value}`, value);

    const exportHref = useMemo(() => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && String(value) !== '') {
                params.set(key, String(value));
            }
        });
        const query = params.toString();
        return `${route('admin.bug-reports.export')}${query ? `?${query}` : ''}`;
    }, [filters]);

    // Toolbar state
    const [viewMode, setViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [search, setSearch] = useState(String(filters.search ?? ''));
    const debouncedSearch = useDebounce(search, 800);
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(
        new Set(['public_id', 'status', 'severity', 'module', 'summary', 'reporter', 'assignee', 'created']),
    );

    const toArray = (value: string | number | undefined): string[] => {
        if (value === undefined || value === null || String(value) === '') return [];
        return [String(value)];
    };

    const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(() => ({
        status: toArray(filters.status),
        severity: toArray(filters.severity),
        reporter: toArray(filters.reporter_user_id),
        assignee: toArray(filters.assigned_to_user_id),
    }));

    const [dateFrom, setDateFrom] = useState(String(filters.created_from ?? ''));
    const [dateTo, setDateTo] = useState(String(filters.created_to ?? ''));

    const sortColumn = String(filters.sort ?? 'created_at');
    const sortDirection = (filters.direction === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc';
    const tableSortColumn = backendSortToColumn[sortColumn] ?? null;
    const selectedPerPage = Number(filters.per_page ?? pagination.per_page ?? 25);

    useEffect(() => {
        setViewMode((current) => (isMobile && current === 'table' ? 'grid' : current));
    }, [isMobile]);

    useEffect(() => {
        const currentSearch = String(filters.search ?? '');
        if (currentSearch === debouncedSearch) return;
        applyFilters({ search: debouncedSearch || undefined, page: 1 });
    }, [applyFilters, debouncedSearch, filters.search]);

    useEffect(() => {
        setSearch(String(filters.search ?? ''));
        setColumnFilters({
            status: toArray(filters.status),
            severity: toArray(filters.severity),
            reporter: toArray(filters.reporter_user_id),
            assignee: toArray(filters.assigned_to_user_id),
        });
        setDateFrom(String(filters.created_from ?? ''));
        setDateTo(String(filters.created_to ?? ''));
    }, [
        filters.search,
        filters.status,
        filters.severity,
        filters.reporter_user_id,
        filters.assigned_to_user_id,
        filters.created_from,
        filters.created_to,
    ]);

    const metricCards = useMemo(
        () => [
            { label: t('bug_reports.metrics.new', 'New'), value: metrics.new, icon: AlertCircle, color: 'text-info-subtle-foreground' },
            { label: t('bug_reports.metrics.in_review', 'In Review'), value: metrics.in_review, icon: Clock, color: 'text-info-subtle-foreground' },
            { label: t('bug_reports.metrics.critical', 'Critical'), value: metrics.critical, icon: TrendingUp, color: 'text-destructive' },
            {
                label: t('bug_reports.metrics.resolved_this_month', 'Resolved This Month'),
                value: metrics.resolved_this_month,
                icon: CheckCircle2,
                color: 'text-success-subtle-foreground',
            },
        ],
        [metrics, t],
    );

    const quickFilterDefinitions = [
        { id: 'status', label: t('bug_reports.filters.status', 'Status') },
        { id: 'severity', label: t('bug_reports.filters.severity', 'Severity') },
        // Reporter/assignee filtering only makes sense for admins who see everyone's reports.
        ...(canSeeAll
            ? [
                  { id: 'reporter', label: t('bug_reports.filters.reporter', 'Reporter') },
                  { id: 'assignee', label: t('bug_reports.filters.assignee', 'Assignee') },
              ]
            : []),
    ];

    const columnDefinitions = [
        { id: 'public_id', label: t('bug_reports.table.id', 'ID') },
        { id: 'status', label: t('bug_reports.table.status', 'Status') },
        { id: 'severity', label: t('bug_reports.table.severity', 'Severity') },
        { id: 'module', label: t('bug_reports.table.module', 'Module') },
        { id: 'summary', label: t('bug_reports.table.summary', 'Summary') },
        ...(canSeeAll
            ? [
                  { id: 'reporter', label: t('bug_reports.table.reporter', 'Reporter') },
                  { id: 'assignee', label: t('bug_reports.table.assignee', 'Assignee') },
              ]
            : []),
        { id: 'created', label: t('bug_reports.table.created', 'Created') },
    ];

    const columnFilterOptions = useMemo<Record<string, Array<{ value: string; label: string }>>>(() => {
        const userOptions = filterOptions.users.map((u) => ({ value: u.id, label: u.name }));
        return {
            status: filterOptions.statuses.map((value) => ({ value, label: statusLabel(value) })),
            severity: filterOptions.severities.map((value) => ({ value, label: severityLabel(value) })),
            reporter: userOptions,
            assignee: userOptions,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterOptions, t]);

    const handleFilterChange = (column: string, values: string[]) => {
        setColumnFilters((prev) => ({ ...prev, [column]: values }));
        const backendKey = filterBackendKey[column as FilterColumn];
        if (backendKey) {
            applyFilters({ [backendKey]: values[0] || undefined, page: 1 });
        }
    };

    // Bug-report filters are single-value, so selecting an option replaces the previous one.
    const toggleSingleFilter = (column: string, value: string) => {
        const current = columnFilters[column] || [];
        const nextValues = current.includes(value) ? [] : [value];
        handleFilterChange(column, nextValues);
    };

    const clearFilters = () => {
        setColumnFilters({ status: [], severity: [], reporter: [], assignee: [] });
        setSearch('');
        setDateFrom('');
        setDateTo('');
        applyFilters({
            status: undefined,
            severity: undefined,
            reporter_user_id: undefined,
            assigned_to_user_id: undefined,
            created_from: undefined,
            created_to: undefined,
            search: undefined,
            page: 1,
        });
    };

    const quickFilterNames = quickFilterDefinitions.filter((q) => (columnFilters[q.id] || []).length > 0).map((q) => q.label);
    const quickFilterButtonLabel =
        quickFilterNames.length > 0
            ? `${t('bug_reports.index.toolbar.quick_filters', 'Quick filters')}: ${quickFilterNames.join(', ')}`
            : t('bug_reports.index.filters.all_reports', 'All Bug Reports');

    const sortableColumns = [
        { id: 'created_at', label: t('bug_reports.sort.created_at', 'Created at') },
        { id: 'updated_at', label: t('bug_reports.sort.updated_at', 'Updated at') },
        { id: 'status', label: t('bug_reports.sort.status', 'Status') },
        { id: 'module', label: t('bug_reports.sort.module', 'Module') },
        { id: 'priority', label: t('bug_reports.sort.priority', 'Priority') },
        { id: 'public_id', label: t('bug_reports.sort.public_id', 'Report ID') },
    ];

    // The toolbar's sort picker speaks backend keys directly.
    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        applyFilters({ sort: column, direction, page: 1 });
    };

    // The table's column headers speak column ids; translate to a backend key.
    const handleTableSortChange = (column: string, direction: 'asc' | 'desc') => {
        applyFilters({ sort: columnToBackendSort[column] ?? 'created_at', direction, page: 1 });
    };

    const clearSort = () => {
        applyFilters({ sort: 'created_at', direction: 'desc', page: 1 });
    };

    const handleColumnShow = (column: string) => {
        setVisibleColumns((prev) => new Set(prev).add(column));
    };

    const handleColumnHide = (column: string) => {
        setVisibleColumns((prev) => {
            const next = new Set(prev);
            next.delete(column);
            return next;
        });
    };

    const handleDateFromChange = (value: string) => {
        setDateFrom(value);
        applyFilters({ created_from: value || undefined, page: 1 });
    };

    const handleDateToChange = (value: string) => {
        setDateTo(value);
        applyFilters({ created_to: value || undefined, page: 1 });
    };

    const handleResetDates = () => {
        setDateFrom('');
        setDateTo('');
        applyFilters({ created_from: undefined, created_to: undefined, page: 1 });
    };

    const breadcrumbs = [{ title: t('bug_reports.title', 'Bug Reports'), href: '/admin/bug-reports' }];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('bug_reports.title', 'Bug Reports')} />

            <PageHeader
                title={t('bug_reports.title', 'Bug Reports')}
                subtitle={
                    canSeeAll
                        ? t('bug_reports.subtitle', 'Review, triage, and manage CRM bug reports.')
                        : t('bug_reports.subtitle_own', 'Track the status of the bugs you reported.')
                }
                actions={
                    canExport ? (
                        <Button asChild variant="outline">
                            <a href={exportHref}>
                                <Download className="mr-2 h-4 w-4" />
                                {t('bug_reports.actions.export', 'Export Markdown')}
                            </a>
                        </Button>
                    ) : undefined
                }
            />

            <PageContent>
                <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {metricCards.map((metric) => {
                        const Icon = metric.icon;
                        return (
                            <div key={metric.label} className="bg-card rounded-xl border p-5">
                                <div className="flex items-center justify-between">
                                    <p className="text-muted-foreground text-sm">{metric.label}</p>
                                    <Icon className={`h-4 w-4 ${metric.color}`} />
                                </div>
                                <p className="mt-2 text-2xl font-bold">{metric.value}</p>
                            </div>
                        );
                    })}
                </div>

                <GenericToolbar
                    quickFilterButtonLabel={quickFilterButtonLabel}
                    quickFilterDefinitions={quickFilterDefinitions}
                    columnDefinitions={columnDefinitions}
                    columnFilterOptions={columnFilterOptions}
                    columnFilters={columnFilters}
                    columnFilterSearch={columnFilterSearch}
                    setColumnFilterSearch={setColumnFilterSearch}
                    toggleSingleFilter={toggleSingleFilter}
                    handleFilterChange={handleFilterChange}
                    clearFilters={clearFilters}
                    isFilterOpen={isFilterOpen}
                    setIsFilterOpen={setIsFilterOpen}
                    isSortOpen={isSortOpen}
                    setIsSortOpen={setIsSortOpen}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    handleSortChange={handleSortChange}
                    clearSort={clearSort}
                    search={search}
                    setSearch={setSearch}
                    perPage={selectedPerPage}
                    onPerPageChange={(perPage) => applyFilters({ per_page: perPage, page: 1 })}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    visibleColumns={visibleColumns}
                    handleColumnShow={handleColumnShow}
                    handleColumnHide={handleColumnHide}
                    filteredCount={pagination.total}
                    resourceName="bug_reports"
                    resourceNamePlural={t('bug_reports.index.reports_plural', 'reports')}
                    sortableColumns={sortableColumns}
                    excludeFromMoreFilters={['status', 'severity', 'reporter', 'assignee']}
                    customFilters={
                        <div className="space-y-3">
                            <div className="flex items-center justify-between text-sm font-medium">
                                <span>{t('bug_reports.filters.date_range', 'Date range')}</span>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground h-auto px-2 py-1 text-xs"
                                    disabled={!dateFrom && !dateTo}
                                    onClick={handleResetDates}
                                >
                                    {t('common.reset', 'Reset')}
                                </Button>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="grid gap-1">
                                    <label className="text-muted-foreground text-xs">{t('bug_reports.filters.from_date', 'From date')}</label>
                                    <DatePicker
                                        value={dateFrom}
                                        onValueChange={handleDateFromChange}
                                        placeholder={t('common.pick_date', 'Pick a date')}
                                    />
                                </div>

                                <div className="grid gap-1">
                                    <label className="text-muted-foreground text-xs">{t('bug_reports.filters.to_date', 'To date')}</label>
                                    <DatePicker
                                        value={dateTo}
                                        onValueChange={handleDateToChange}
                                        placeholder={t('common.pick_date', 'Pick a date')}
                                    />
                                </div>
                            </div>
                        </div>
                    }
                />

                {viewMode === 'grid' ? (
                    reports.length === 0 ? (
                        <Card>
                            <CardContent className="py-12 text-center">
                                <p className="text-muted-foreground">{t('bug_reports.table.empty', 'No bug reports found.')}</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {reports.map((report) => (
                                <BugReportCard key={report.id} report={report} canOpen={canEdit} />
                            ))}
                        </div>
                    )
                ) : (
                    <BugReportTable
                        reports={reports}
                        canOpen={canEdit}
                        canSeeAll={canSeeAll}
                        visibleColumns={visibleColumns}
                        columnFilters={columnFilters}
                        columnFilterOptions={columnFilterOptions}
                        onFilterChange={handleFilterChange}
                        sortColumn={tableSortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleTableSortChange}
                        onColumnHide={handleColumnHide}
                    />
                )}

                <PaginationControls
                    pagination={pagination}
                    loading={loading}
                    onPageChange={(page) => applyFilters({ page })}
                    onPerPageChange={(perPage) => applyFilters({ per_page: perPage, page: 1 })}
                    className="mt-6"
                />
            </PageContent>
        </AppLayout>
    );
}
